import { Button, DatePicker, Empty, Radio, Form, Select, usePaging } from '@maxtropy/components';
import { RadioChangeEvent, Space, Spin } from 'antd';
import React, { FC, useEffect, useMemo, useState } from 'react';
import styles from './index.module.scss';
import {
  apiV2EnergyMediumListByUnitLevel1Post,
  apiV2EnergyUnitConsumptionRankingPost,
  V2EnergyUnitConsumptionRankingPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { useRequest } from 'ahooks';
import { EnergyAnalysisRankingContext } from '../..';
import {
  DateType,
  DateTypeDisplay,
  getDefaultTime,
  RankMethod,
  RankMethodDisplay,
  RankMethodType,
  RankMethodTypeDisplay,
} from '../../utils';
import dayjs, { Dayjs } from 'dayjs';
import Chart from './components/Chart';
import RankTable from './components/RankTable';
import { MeterType } from '@/api/energyConsumption';
import { DataPeriodListItem, getDataQueryPeriodList } from '@/api/energyOverview';
import { Link } from 'react-router-dom';
import { SettingOutlined } from '@ant-design/icons';
import CustomDateModal from './components/CustomModal';
import { isEmpty } from 'lodash-es';

export interface CustomerRequest {
  timeResolution: DateType;
  currentPeriod: [Dayjs, Dayjs];
  comparisonPeriod?: [Dayjs, Dayjs];
}

const RightContent = () => {
  const { unitIds, groupId } = React.useContext(EnergyAnalysisRankingContext);
  const [timeResolution, setTimeResolution] = useState<DateType>(DateType.DAY);
  const [displayValue, setDisplayValue] = useState<Dayjs | undefined>(dayjs()); // 日期选择器默认选择当天
  const [type, setType] = useState<string>('chart');
  const [selectedConsumption, setSelectedConsumption] = useState<number | undefined>(); // 能源介质选择
  const [rankMethod, setRankMethod] = useState<RankMethod>(RankMethod.BY_USAGE); // 排名方式
  const [energyMethod, setEnergyMethod] = useState<RankMethodType>(RankMethodType.ALL); // 尖峰平谷筛选方式
  const [dataQueryPeriodId, setDataQueryPeriodId] = useState<number>();
  const [dataQueryPeriodName, setDataQueryPeriodName] = useState<string>();
  const [dataPeriodList, setDataPeriodList] = useState<DataPeriodListItem[]>([]);
  const [openCustom, setOpenCustom] = useState<boolean>();
  const [customerRequest, setCustomerRequest] = useState<CustomerRequest>();
  const [data, setData] = useState<V2EnergyUnitConsumptionRankingPostResponse['list']>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const pagingInfo = usePaging(50);
  const { pageOffset, pageSize } = pagingInfo; // 分页的一些配置

  // 获取能源介质
  const { data: energyMediumList } = useRequest(
    async () => {
      const res = await apiV2EnergyMediumListByUnitLevel1Post({ energyUnitGroupId: groupId! });
      return res.list;
    },
    {
      ready: !!groupId,
      refreshDeps: [groupId],
    }
  );

  // 获取图表和列表数据

  useEffect(() => {
    if (
      (timeResolution !== DateType.CUSTOM && displayValue) ||
      (timeResolution === DateType.CUSTOM && !isEmpty(customerRequest))
    ) {
      setLoading(true);
      apiV2EnergyUnitConsumptionRankingPost({
        energyUnitIdList: unitIds as number[],
        fromTime: getDefaultTime(timeResolution, displayValue!).startTime,
        toTime: getDefaultTime(timeResolution, displayValue!).endTime,
        energyMediumId: selectedConsumption,
        resolutionType: timeResolution,
        periodIds: dataQueryPeriodId ? [dataQueryPeriodId] : undefined,
        electricityTimePeriodType: energyMethod,
        energyUnitGroupId: groupId,
        customerRequest:
          timeResolution === DateType.CUSTOM
            ? {
                resolutionType: customerRequest?.timeResolution,
                fromTime: getDefaultTime(customerRequest!.timeResolution, customerRequest!.currentPeriod[0]).startTime,
                toTime: getDefaultTime(customerRequest!.timeResolution, customerRequest!.currentPeriod[1]).endTime,
                customFromTime:
                  customerRequest?.comparisonPeriod &&
                  getDefaultTime(customerRequest.timeResolution, customerRequest.comparisonPeriod[0]).startTime,
                customToTime:
                  customerRequest?.comparisonPeriod &&
                  getDefaultTime(customerRequest.timeResolution, customerRequest.comparisonPeriod[1]).endTime,
              }
            : undefined,
        type: rankMethod,
        page: type === 'chart' ? 1 : pageOffset,
        size: type === 'chart' ? 20 : pageSize,
      })
        .then(res => {
          setData(res.list ?? []);
        })
        .finally(() => setLoading(false));
    }
  }, [
    groupId,
    unitIds,
    displayValue,
    selectedConsumption,
    timeResolution,
    energyMethod,
    pageOffset,
    pageSize,
    dataQueryPeriodId,
    rankMethod,
    customerRequest,
  ]);

  useEffect(() => {
    let startTime = getDefaultTime(timeResolution, displayValue!).startTime;
    let endTime = getDefaultTime(timeResolution, displayValue!).endTime;

    if (timeResolution === DateType.CUSTOM && customerRequest?.timeResolution) {
      startTime = getDefaultTime(customerRequest?.timeResolution, customerRequest?.currentPeriod[0]).startTime;
      endTime = getDefaultTime(customerRequest?.timeResolution, customerRequest?.currentPeriod[1]).endTime;
    }

    startTime &&
      endTime &&
      getDataQueryPeriodList({
        startTime,
        endTime,
      }).then(res => {
        setDataPeriodList(res.dataPeriodList ?? []);
      });
  }, [customerRequest, timeResolution, displayValue]);

  useEffect(() => {
    setDataQueryPeriodId(undefined);
    setDataQueryPeriodName(undefined);
  }, [groupId, timeResolution, displayValue, unitIds, selectedConsumption]);

  // 默认选择第一个能源介质
  useEffect(() => {
    if (energyMediumList?.length === 0 || !energyMediumList) return;
    setSelectedConsumption(energyMediumList[0].id);
  }, [energyMediumList]);

  // 切换到电能的时候选择全选
  useEffect(() => {
    if (selectedConsumption !== MeterType.ELECTRICAL_ENERGY) {
      setEnergyMethod(RankMethodType.ALL);
    }
  }, [selectedConsumption]);

  // tabs format
  const energyMediumListTabs = useMemo(() => {
    return (energyMediumList ?? []).map(item => ({
      name: item.name,
      id: item.id,
    }));
  }, [energyMediumList]);

  // render picker
  const picker = useMemo(() => {
    if (timeResolution === DateType.MONTH) {
      return 'month';
    } else if (timeResolution === DateType.WEEK) {
      return 'week';
    } else if (timeResolution === DateType.YEAR) {
      return 'year';
    } else {
      return 'date';
    }
  }, [timeResolution]);

  const onRadioChange = (e: RadioChangeEvent) => {
    const value: DateType = e.target.value;
    if (value !== DateType.CUSTOM) {
      setCustomerRequest(undefined);
      setTimeResolution(value);
      setDisplayValue(dayjs());
    } else {
      setTimeResolution(value);
      setDisplayValue(undefined);
    }
  };

  const disabledDate = (current: Dayjs) => {
    return current && current >= dayjs().endOf('day');
  };

  const renderRankMethod = () => {
    if (selectedConsumption === MeterType.ELECTRICAL_ENERGY) {
      return (
        <>
          <Radio.Button value={RankMethodType.ALL}>
            {RankMethodTypeDisplay[RankMethodType.ALL]}
            {RankMethodDisplay[rankMethod]}
          </Radio.Button>
          <Radio.Button value={RankMethodType.SHARP}>
            {RankMethodTypeDisplay[RankMethodType.SHARP]}-{RankMethodDisplay[rankMethod]}
          </Radio.Button>
          <Radio.Button value={RankMethodType.PEAK}>
            {RankMethodTypeDisplay[RankMethodType.PEAK]}-{RankMethodDisplay[rankMethod]}
          </Radio.Button>
          <Radio.Button value={RankMethodType.AVERAGE}>
            {RankMethodTypeDisplay[RankMethodType.AVERAGE]}-{RankMethodDisplay[rankMethod]}
          </Radio.Button>
          <Radio.Button value={RankMethodType.VALLEY}>
            {RankMethodTypeDisplay[RankMethodType.VALLEY]}-{RankMethodDisplay[rankMethod]}
          </Radio.Button>
        </>
      );
    } else {
      return (
        <>
          <Radio.Button value={RankMethodType.ALL}>
            {RankMethodTypeDisplay[RankMethodType.ALL]}
            {RankMethodDisplay[rankMethod]}
          </Radio.Button>
        </>
      );
    }
  };

  return (
    <Spin spinning={loading}>
      <Space className={styles.tab_box} size="small" wrap>
        {energyMediumListTabs.map(medium => (
          <Button
            key={medium.id}
            style={{ paddingLeft: 12, paddingRight: 16 }}
            onClick={() => {
              setSelectedConsumption(medium.id);
            }}
            className={selectedConsumption === medium.id ? styles.titleButtonChecked : styles.titleButtonUnChecked}
          >
            <Space size="small" align="center">
              <span>{medium.name}</span>
            </Space>
          </Button>
        ))}
      </Space>
      <div className={styles.time_box}>
        {/* 日期选择器 */}
        <Space size={8} style={{ padding: '0px 20px' }}>
          <Radio.Group onChange={onRadioChange} value={timeResolution} buttonStyle="solid">
            <Radio.Button value={DateType.DAY}>{DateTypeDisplay[DateType.DAY]}</Radio.Button>
            <Radio.Button value={DateType.WEEK}>{DateTypeDisplay[DateType.WEEK]}</Radio.Button>
            <Radio.Button value={DateType.MONTH}>{DateTypeDisplay[DateType.MONTH]}</Radio.Button>
            <Radio.Button value={DateType.YEAR}>{DateTypeDisplay[DateType.YEAR]}</Radio.Button>
            <Radio.Button value={DateType.CUSTOM}>{DateTypeDisplay[DateType.CUSTOM]}</Radio.Button>
          </Radio.Group>
          {timeResolution === DateType.CUSTOM ? (
            <Button
              type="link"
              icon={<SettingOutlined />}
              onClick={() => setOpenCustom(true)}
              style={{ padding: 0, gap: 0 }}
            >
              自定义维度设置
            </Button>
          ) : (
            <DatePicker
              style={{ width: '250px' }}
              picker={picker}
              value={displayValue}
              disabledDate={disabledDate}
              onChange={setDisplayValue}
              allowClear={false}
            />
          )}

          <Form.Item label="数据查询时段" style={{ marginBottom: 0, marginLeft: 8 }}>
            <Select
              placeholder="请选择"
              style={{ width: 200 }}
              value={dataQueryPeriodId}
              onChange={(value: number, option) => {
                setDataQueryPeriodId(value);
                setDataQueryPeriodName((option as unknown as any).label);
              }}
              options={dataPeriodList.map(item => ({ value: item.id, label: item.name }))}
              allowClear
            />
          </Form.Item>
        </Space>
        <Space size={8} style={{ marginRight: 20 }}>
          <Button type="primary">
            <Link to={`/energy/analysis/ranking/group`}>配置分组</Link>
          </Button>
        </Space>
      </div>
      <div className={styles.btn_box}>
        <Space size={8} style={{ padding: '0px 20px' }}>
          <div className={styles.filterArea}>
            <div className={styles.option}>排名方式：</div>
            <Select
              placeholder="请选择"
              className={styles.selectBox}
              value={rankMethod}
              allowClear={false}
              onChange={val => {
                setRankMethod(val);
              }}
            >
              <Select.Option value={RankMethod.BY_USAGE}>按用量排名</Select.Option>
              {timeResolution === DateType.CUSTOM &&
              !isEmpty(customerRequest) &&
              isEmpty(customerRequest.comparisonPeriod) ? null : (
                <Select.Option value={RankMethod.BY_INCREMENT}>按增量排名</Select.Option>
              )}
            </Select>
          </div>

          <div className={styles.typeChangeStyle}>
            <Radio.Group
              buttonStyle="solid"
              value={energyMethod}
              onChange={(e: RadioChangeEvent) => setEnergyMethod(e.target.value)}
            >
              {renderRankMethod()}
            </Radio.Group>
          </div>
        </Space>
        {/* 切换模式按钮 */}
        <div className={styles.typeChangeStyle}>
          <Radio.Group buttonStyle="solid" value={type} onChange={(e: RadioChangeEvent) => setType(e.target.value)}>
            <Radio.Button value="chart">图表视图</Radio.Button>
            <Radio.Button value="list">列表视图</Radio.Button>
          </Radio.Group>
        </div>
      </div>
      {!unitIds || unitIds.length === 0 ? (
        <Empty />
      ) : type === 'chart' ? (
        <div className={styles.chart_box}>
          <Chart
            data={data}
            loading={loading}
            rankMethod={rankMethod}
            timeResolution={timeResolution}
            energyMethod={energyMethod}
            dataQueryPeriodId={dataQueryPeriodId}
            dataQueryPeriodName={dataQueryPeriodName}
            selectedConsumption={selectedConsumption}
            displayValue={displayValue}
            energyUnitGroupId={groupId}
            customerRequest={customerRequest}
          />
        </div>
      ) : (
        <div className={styles.table_box}>
          <RankTable
            data={(data ?? []).map((item, index) => ({
              ...item,
              rankIndex: index + 1,
            }))}
            loading={loading}
            rankMethod={rankMethod}
            customerRequest={customerRequest}
            timeResolution={timeResolution}
            displayValue={displayValue}
            energyMethod={energyMethod}
            dataQueryPeriodId={dataQueryPeriodId}
            dataQueryPeriodName={dataQueryPeriodName}
            selectedConsumption={selectedConsumption}
            energyUnitGroupId={groupId}
            pagingInfo={pagingInfo}
          />
        </div>
      )}
      {openCustom && (
        <CustomDateModal
          onOk={(customerRequest: any) => {
            setCustomerRequest(customerRequest);
            setOpenCustom(false);
          }}
          customerRequest={customerRequest}
          onCancel={() => setOpenCustom(false)}
          rankMethod={rankMethod}
        />
      )}
    </Spin>
  );
};

export default RightContent;
